import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Users, Briefcase, Star } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf0aab81-969a-4e9c-9793-f40790513670/artifacts/8sw178l3_LOGO_CLICQCONSULT.png";
const LOGO_SMALL_URL = "https://customer-assets.emergentagent.com/job_cf0aab81-969a-4e9c-9793-f40790513670/artifacts/hkz47slg_LOGO_CLICQ_SMALL.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: 'blur(10px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)' }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const services = [
{
  icon: Shield,
  title: 'Consolidation des actifs',
  description: 'Suivi régulier et centralisation de votre patrimoine global.'
},
{
  icon: Users,
  title: 'Transmission patrimoniale',
  description: 'Accompagnement stratégique pour la transmission de vos actifs.'
},
{
  icon: Briefcase,
  title: 'Suivi administratif',
  description: 'Gestion administrative et financière personnalisée.'
},
{
  icon: Star,
  title: 'Conciergerie privée',
  description: 'Services exclusifs pour répondre à vos besoins quotidiens.'
}];



export default function HomePage() {
  return (
    <div className="page-wrapper" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden" data-testid="hero-section">
        {/* Background Image with subtle movement */}
        <motion.div
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_cf0aab81-969a-4e9c-9793-f40790513670/artifacts/ozk0drws_IMAGE_PAGE_GARDE_CONSOLIDATION.png')`,
            opacity: 0.75
          }}
          animate={{
            scale: [1.1, 1.15, 1.1],
            x: [0, 10, 0],
            y: [0, -5, 0]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }} />


        
        {/* Overlay */}
        <div className="hero-overlay" />
        
        {/* Content */}
        <div className="hero-content flex flex-col items-center justify-center min-h-screen px-6 text-center">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="max-w-4xl mx-auto">

            {/* Logo centered - Symbole & doré */}
            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.8 }}
              className="mb-8">
              <img
                src={LOGO_SMALL_URL}
                alt="CLICQCONSULT"
                className="h-16 md:h-20 lg:h-24 w-auto mx-auto"
                style={{ filter: 'sepia(30%) saturate(80%) brightness(95%)' }}
                data-testid="hero-logo" />
            </motion.div>

            {/* Tagline */}
            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-[#C5A059] text-xs md:text-sm tracking-[0.3em] uppercase mb-8">
              Family Office Franco-Belge
            </motion.p>

            {/* Decorative Line */}
            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="gold-line mx-auto mb-8" />

            {/* Main Heading */}
            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="font-serif text-4xl md:text-5xl lg:text-7xl text-[#FDFBF7] font-medium tracking-tight leading-tight mb-8">

              L'<span style={{ marginLeft: '0.08em' }}>excellence</span>
              <br />
              <span className="text-gold-gradient">à votre service</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-[#FDFBF7]/70 text-lg md:text-xl font-light max-w-2xl mx-auto mb-12 leading-relaxed">

              Depuis plus de dix ans, nous accompagnons des familles dans la gestion et la préservation de leur patrimoine en Belgique et en France.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">

              <Link
                to="/services"
                className="btn-gold inline-flex items-center gap-3"
                data-testid="hero-cta-services">

                Nos services
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                className="btn-outline-gold"
                data-testid="hero-cta-contact">

                Nous contacter
              </Link>
            </motion.div>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="absolute bottom-12 left-1/2 -translate-x-1/2">

            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="gold-line-vertical" />

          </motion.div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="py-24 md:py-32 bg-[#FDFBF7]" data-testid="services-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 md:mb-20">

            <p className="uppercase text-xs tracking-[0.3em] mb-4 text-[#C5A059]">
              Expertises
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-[#0B1221]">
              Nos domaines d'intervention
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">

            {services.map((service, index) =>
            <motion.div
              key={index}
              variants={fadeUp}
              transition={{ duration: 0.6 }}
              className="service-card p-8 md:p-10"
              data-testid={`service-card-${index}`}>

                <service.icon
                size={32}
                className="text-[#C5A059] mb-6"
                strokeWidth={1.5} />

                <h3 className="md:text-2xl !font-serif !text-xl mb-4 text-[#0B1221]">
                  {service.title}
                </h3>
                <p className="text-[#64748B] text-sm leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mt-12 md:mt-16">

            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-[#0B1221] text-sm tracking-wider uppercase font-medium hover:text-[#C5A059] transition-colors"
              data-testid="view-all-services">

              Découvrir tous nos services
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Franco-Belgian Section */}
      <section className="py-24 md:py-32 bg-[#F5F2EB]" data-testid="franco-belgian-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1">

              <div className="relative overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1761437856297-e7d0191f9a42?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Art abstrait doré"
                  className="w-full h-[400px] md:h-[500px] object-cover" />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-1 lg:order-2">

              <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-4">
                Notre spécialité
              </p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#0B1221] font-medium tracking-tight mb-6">
                Expertise<br />Franco-Belge
              </h2>
              <div className="gold-line mb-8" />
              <p className="!text-xs mb-6 text-[#64748B]">


              </p>
              <p className="text-[#64748B] leading-relaxed mb-8">Notre positionnement unique entre Paris et Bruxelles nous permet d'offrir une expertise transfrontalière incomparable. 
Nous maîtrisons les subtilités juridiques, fiscales et culturelles des deux pays pour faciliter vos projets internationaux.


              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-[#0B1221] text-sm tracking-wider uppercase font-medium hover:text-[#C5A059] transition-colors"
                data-testid="learn-more-about">

                En savoir plus
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0B1221]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}>

            <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-4">
              Prenons contact
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium mb-6 text-[#FDFBF7]">
              Rencontrons-nous
            </h2>
            <p className="max-w-xl text-lg mx-auto mb-10 text-[#FDFBF7]/70 leading-relaxed">
              Chaque famille est unique. Discutons ensemble de vos desiderata et de la manière dont nous pouvons vous accompagner.
            </p>
            <Link
              to="/contact"
              className="btn-gold inline-flex items-center gap-3"
              data-testid="cta-contact-button">

              Nous contacter
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>);

}